"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MFInternal = void 0;
const Kind_1 = require("./Kind");
const ensureCase_1 = require("./ensureCase");
const parse_1 = require("./parse");
const flatten_1 = require("./util/flatten");
const getEA_1 = require("./util/getEA");
const getElements_1 = require("./util/getElements");
const getInfo_1 = require("./util/getInfo");
const getIsotopesInfo_1 = require("./util/getIsotopesInfo");
const partsToDisplay_1 = require("./util/partsToDisplay");
const partsToMF_1 = require("./util/partsToMF");
const toDisplay_1 = require("./util/toDisplay");
const toHtml_1 = require("./util/toHtml");
const toParts_1 = require("./util/toParts");
const toText_1 = require("./util/toText");
/** @typedef {import('./util/getIsotopesInfo.types').IsotopesInfo} IsotopesInfo */
/** @typedef {import('./util/getInfo.types').MFInfo} MFInfo */
/** @typedef {import('./util/getInfo.types').MFInfoWithParts} MFInfoWithParts */
/**
 * Class allowing to deal with molecular formula and derived information
 */
class MFInternal {
    constructor(mf, options = {}) {
        const { flattenLimit = 0 } = options;
        if (options.ensureCase) {
            mf = (0, ensureCase_1.ensureCase)(mf);
        }
        this.parsed = (0, parse_1.parse)(mf);
        // This is definitely not the fastest way to do it but it allows to flatten the formula if needed and then parse it again to have a clean structure without multiplier ranges. We can consider doing it in one pass in the future if performance is an issue.
        if (flattenLimit > 0 &&
            this.parsed.some((entry) => entry.kind === Kind_1.Kind.MULTIPLIER_RANGE)) {
            try {
                const flattened = (0, flatten_1.flatten)(this.parsed, { limit: flattenLimit });
                this.parsed = (0, parse_1.parse)(flattened.join('.'));
            }
            catch (error) {
                // eslint-disable-next-line no-console
                console.error(error.toString());
            }
        }
        this.cache = {};
    }
    /**
     * Returns an array of objects with kind and value that can be used to easily
     * display the molecular formula.
     * @returns {{kind: string, value: string}[]}
     */
    toDisplay() {
        if (!this.cache.displayed)
            this.cache.displayed = (0, toDisplay_1.toDisplay)(this.parsed);
        return this.cache.displayed;
    }
    /**
     * Returns a string that represents the molecular formula adding subscript and superscript in HTML.
     * @returns {string}
     */
    toHtml() {
        if (!this.cache.html) {
            this.toDisplay();
            this.cache.html = (0, toHtml_1.toHtml)(this.cache.displayed);
        }
        return this.cache.html;
    }
    /**
     * Returns a string that represents the molecular formula adding subscript and superscript
     * using Unicode characters. This can not be parsed anymore so kind of dead end ...
     * @returns {string}
     */
    toText() {
        if (!this.cache.text) {
            this.toDisplay();
            this.cache.text = (0, toText_1.toText)(this.cache.displayed);
        }
        return this.cache.text;
    }
    /**
     * Similar to toText but returns a canonic string in which the atoms are sorted using the Hill system
     * @returns {string}
     */
    toCanonicText() {
        if (!this.cache.canonicText) {
            this.cache.canonicText = new MFInternal(this.toMF()).toText();
        }
        return this.cache.canonicText;
    }
    toParts(options) {
        if (options) {
            return (0, toParts_1.toParts)(this.parsed, options);
        }
        // we don't want to cache the parts if we are using options
        if (!this.cache.parts) {
            this.cache.parts = (0, toParts_1.toParts)(this.parsed, options);
        }
        return this.cache.parts;
    }
    /**
     * Returns an object with the global MF, global charge, monoisotopic mass and mass
     * as well as the same information for all the parts
     * @param {object} [options={}] options
     * @param {object} [options.customUnsaturations={}] custom unsaturations
     * @param {string} [options.emFieldName='monoisotopicMass'] name of the monoisotopic mass field
     * @param {string} [options.msemFieldName='observedMonoisotopicMass'] name of the observed monoisotopic mass field
     * @returns {MFInfo|MFInfoWithParts}
     */
    getInfo(options = {}) {
        if (!this.cache.info) {
            this.toParts();
            this.cache.info = (0, getInfo_1.getInfo)(this.cache.parts, options);
        }
        return this.cache.info;
    }
    /**
     * Returns an object with the elemental analysis
     * @returns {*[]}
     */
    getEA() {
        if (!this.cache.ea) {
            this.toParts();
            this.cache.ea = (0, getEA_1.getEA)(this.cache.parts);
        }
        return this.cache.ea;
    }
    /**
     * Get the different elements for each part
     * @returns {*[]}
     */
    getElements() {
        if (!this.cache.elements) {
            this.toParts();
            this.cache.elements = (0, getElements_1.getElements)(this.cache.parts);
        }
        return this.cache.elements;
    }
    /**
     * Returns an array with each atom and isotopic composition
     * @returns {IsotopesInfo}
     */
    getIsotopesInfo(options = {}) {
        if (!this.cache.isotopesInfo) {
            this.toParts();
            this.cache.isotopesInfo = (0, getIsotopesInfo_1.getIsotopesInfo)(this.cache.parts, options);
        }
        return this.cache.isotopesInfo;
    }
    /**
     * Get a canonized MF
     * @returns {string}
     */
    toMF() {
        if (!this.cache.mf) {
            this.toParts();
            this.cache.mf = (0, partsToMF_1.partsToMF)(this.cache.parts);
        }
        return this.cache.mf;
    }
    /**
     * Get a canonized MF
     * @returns {string}
     */
    toNeutralMF() {
        if (!this.cache.neutralMF) {
            this.toParts();
            this.cache.neutralMF = (0, partsToMF_1.partsToMF)(this.cache.parts, { neutral: true });
        }
        return this.cache.neutralMF;
    }
    canonize() {
        this.toParts();
        this.cache.displayed = (0, partsToDisplay_1.partsToDisplay)(this.cache.parts);
        this.cache.html = undefined;
    }
    flatten(options) {
        return (0, flatten_1.flatten)(this.parsed, options);
    }
}
exports.MFInternal = MFInternal;
//# sourceMappingURL=MFInternal.js.map